//Built-in libraries

//Third party libraries
import { ClipboardDocumentCheckIcon, ClipboardDocumentListIcon, IdentificationIcon, ServerIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { postApplication } from '@/api/Rover/ApplicationForm';
import SubmitSuccess from '@/components/Applications/SubmitSuccess';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
//Custom components
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
//Services
import { ApplicationFormProvider, useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import ClientInformation from './ClientInformation';
import RealEstateGoals from './RealEstateGoals';
import Review from './Review';

//STP Libraries

import type { IPostApplicationServicePayload } from '@/api/Rover/ApplicationForm/types';
import type { IApplicationFormProps } from '@/features/ApplicationForm/types';
import DefaultApplicantInformation from '../../Components/DefaultApplicantInformation';
//Assets and Miscellaneous
import { initialValues } from './constants';
import { GStrategiesValidationSchema } from './validationSchema';

//Component Begin

const steps = [
  {
    name: 'Applicant Information',
    sectionId: 'applicantInformation',
    Icon: IdentificationIcon,
    component: <DefaultApplicantInformation />,
  },
  {
    name: 'Client Information',
    sectionId: 'step1',
    Icon: ClipboardDocumentListIcon,
    component: <ClientInformation />,
  },
  {
    name: 'Real Estate Goals',
    sectionId: 'step2',
    Icon: ServerIcon,
    component: <RealEstateGoals />,
  },

  {
    name: 'Review',
    sectionId: 'step3',
    Icon: ClipboardDocumentCheckIcon,
    component: <Review />,
  },
];

const ApplicationFormBase = ({ formData }: IApplicationFormProps) => {
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const { currentStep, setCurrentStep, updateData, data } = useApplicationFormContext();

  const isLast = currentStep === steps.length - 1;

  const submitApplication = useMutation((data: IPostApplicationServicePayload) => postApplication({ data }), {
    onSuccess: () => {
      setIsSubmitSuccess(true);
    },
  });

  const onSubmitHandler = (values: Record<string, any>) => {
    if (!isLast) {
      updateData({
        [steps[currentStep].sectionId]: values[steps[currentStep].sectionId],
      });
      setCurrentStep((prev) => prev + 1);
    } else {
      const applicationData: IPostApplicationServicePayload = {
        formId: formData?.data?.id,
        contactName: data.applicantInformation.fullName,
        contactEmail: data.applicantInformation.contactEmail,
        applicationData: data,
        files: [],
      };
      submitApplication.mutate(applicationData);
    }
  };

  if (isSubmitSuccess) {
    return (
      <SubmitSuccess
        message={
          formData.data.confirmationMessage ?? 'Thank you for your application.You will also receive an email with confirmation number.'
        }
      />
    );
  }

  return (
    <div className='container mx-auto'>
      <h2>{formData?.data?.name}</h2>
      <div className='flex mb-10'>
        <aside className='w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md'>
          <div className='w-full flex justify-center'>
            <img src={formData.data.logo} alt='Form Logo' className='h-14 xl:h-20 object-scale-down ' />
          </div>
          <nav className='space-y-1 py-5'>
            {steps.map(({ name, Icon }, index) => (
              <button
                key={index}
                className={`${
                  currentStep === index ? 'bg-neutralColor-lightest text-primaryColor-normal' : 'text-neutralColor-normal'
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full`}
              >
                <Icon className='shrink-0 -ml-1 mr-3 h-6 w-6' />
                {name}
              </button>
            ))}
          </nav>
        </aside>
        <div className='w-full bg-gray-50'>
          <HookFormProvider
            defaultValues={initialValues}
            mode='all'
            resolver={yupResolver(GStrategiesValidationSchema[currentStep] as any)}
          >
            <HookForm onSubmit={onSubmitHandler}>
              {steps[currentStep].component}
              {isLast && (
                <div className='flex gap-4 justify-end p-6'>
                  <Button
                    label='Previous'
                    btnStyle='neutral'
                    type='button'
                    disabled={submitApplication.isLoading}
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  />
                  <Button
                    label={`${submitApplication.isLoading ? 'Submitting...' : 'Submit'}`}
                    btnStyle='primary'
                    type='submit'
                    disabled={submitApplication.isLoading}
                  />
                </div>
              )}
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export const GStrategiesApplicationForm = ({ ...props }: IApplicationFormProps) => {
  return (
    <ApplicationFormProvider initialValues={initialValues}>
      <ApplicationFormBase {...props} />
    </ApplicationFormProvider>
  );
};
